/**
 * Security & privacy settings — MFA (TOTP), active sessions, email verification,
 * and account deactivation. Available to any signed-in user.
 *
 * Backed by /api/security/* endpoints. Every state change is server-audited.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, ShieldCheck, ShieldOff, LogOut, Trash2, Mail, Copy, RefreshCcw, Download, FileText, Rocket, KeyRound, Phone } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// QR now rendered client-side via qrcode.react — the provisioning URI (which contains the
// plaintext TOTP secret) NEVER leaves the browser. Never revert this to a hosted service.

export default function SecuritySettings() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [mfaStatus, setMfaStatus] = useState({ enabled: false, required: false });
  const [setup, setSetup] = useState(null); // { provisioning_uri, secret }
  const [verifyCode, setVerifyCode] = useState("");
  const [busy, setBusy] = useState("");
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [sessions, setSessions] = useState([]);
  const [revokeAllPw, setRevokeAllPw] = useState("");
  const [deactivatePw, setDeactivatePw] = useState("");
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deletePhrase, setDeletePhrase] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [exitReason, setExitReason] = useState("");
  const [exitNote, setExitNote] = useState("");
  // Change password (self-service, all roles)
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  // Contact details (self-service)
  const [contact, setContact] = useState({ phone: "", address_line1: "", address_line2: "", city: "", postcode: "", country: "" });
  // Batch 10C.1 · communication preferences
  const [commPrefs, setCommPrefs] = useState({ in_app_enabled: true, email_enabled: true, follow_up_reminders_enabled: false, session_review_notifications_enabled: true });
  useEffect(() => {
    api.get("/me/communication-preferences").then((r) => setCommPrefs(r.data.preferences)).catch(() => {});
  }, []);
  const saveCommPref = async (key, value) => {
    const next = { ...commPrefs, [key]: value };
    setCommPrefs(next);
    setBusy("comm-prefs");
    try {
      const r = await api.put("/me/communication-preferences", { [key]: value });
      setCommPrefs(r.data.preferences);
      toast.success("Communication preferences saved");
    } catch (e) {
      setCommPrefs(commPrefs); // revert on failure
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(""); }
  };
  useEffect(() => {
    if (user) setContact({
      phone: user.phone || "", address_line1: user.address_line1 || "", address_line2: user.address_line2 || "",
      city: user.city || "", postcode: user.postcode || "", country: user.country || "",
    });
  }, [user]);
  const saveContact = async () => {
    setBusy("contact");
    try {
      const r = await api.patch("/auth/me/contact", contact);
      if (setUser && user) setUser({ ...user, ...r.data });
      toast.success("Contact details saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(""); }
  };

  const refresh = async () => {
    try {
      const [m, s] = await Promise.all([
        api.get("/security/mfa/status"),
        api.get("/security/sessions"),
      ]);
      setMfaStatus(m.data);
      setSessions(s.data);
    } catch (e) { /* handled by interceptors elsewhere */ }
  };
  useEffect(() => { refresh(); }, []);

  const beginSetup = async () => {
    setBusy("setup");
    try {
      const { data } = await api.post("/security/mfa/setup", {});
      setSetup(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    finally { setBusy(""); }
  };

  const confirmSetup = async () => {
    setBusy("verify");
    try {
      await api.post("/security/mfa/verify", { code: verifyCode.trim() });
      toast.success("Two-factor authentication enabled.");
      setSetup(null); setVerifyCode("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(""); }
  };

  const disableMfa = async () => {
    setBusy("disable");
    try {
      await api.post("/security/mfa/disable", { password: disablePw, code: disableCode.trim() });
      toast.success("Two-factor authentication disabled.");
      setDisablePw(""); setDisableCode("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disable failed"); }
    finally { setBusy(""); }
  };

  const resendEmail = async () => {
    setBusy("resend");
    try {
      const { data } = await api.post("/security/email/resend", {});
      toast.success(data.already_verified ? "Email already verified." : "Verification email sent.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(""); }
  };

  const changePassword = async () => {
    if (pwNew.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pwNew !== pwConfirm) { toast.error("New password and confirmation do not match."); return; }
    if (pwNew === pwCurrent) { toast.error("New password must be different from the current one."); return; }
    setBusy("change-pw");
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password changed. You'll stay signed in on this device.");
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Password change failed"); }
    finally { setBusy(""); }
  };

  const revokeSession = async (id) => {
    setBusy(`revoke-${id}`);
    try {
      await api.delete(`/security/sessions/${id}`);
      toast.success("Session revoked.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
    finally { setBusy(""); }
  };

  const revokeAllOthers = async () => {
    setBusy("revoke-all");
    try {
      const { data } = await api.post("/security/sessions/revoke-others", { password: revokeAllPw });
      toast.success(`Revoked ${data.revoked} other session${data.revoked === 1 ? "" : "s"}.`);
      setRevokeAllPw("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
    finally { setBusy(""); }
  };

  const deactivateAccount = async () => {
    setBusy("deactivate");
    try {
      await api.post("/security/account/deactivate", { password: deactivatePw, confirm: deactivateConfirm });
      toast.success("Account deactivated.");
      await logout();
      nav("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Deactivation failed"); }
    finally { setBusy(""); }
  };

  const deleteAccount = async () => {
    setBusy("delete");
    try {
      await api.delete("/security/account", {
        data: {
          password: deletePw,
          confirm_phrase: deletePhrase,
          exit_reason: exitReason || null,
          exit_note: exitNote.trim() || null,
        },
      });
      toast.success("Account permanently deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deletion failed");
      setBusy("");
      return;
    }
    // Server has removed the user. Attempt a graceful logout, then navigate.
    // Any failure here MUST NOT leave the user pinned to the app — the local
    // token is now orphaned server-side, so we always clear + nav even if
    // /auth/logout errors out.
    try { await logout(); } catch { /* orphaned token — logout call may 401 */ }
    setBusy("");
    nav("/");
  };

  const closeDeleteModal = () => {
    if (busy === "delete") return; // don't allow closing mid-request
    setShowDeleteModal(false);
    setDeletePhrase("");
    setDeletePw("");
    setExitReason("");
    setExitNote("");
  };
  const deleteDialogRef = useModalA11y({ open: showDeleteModal, onClose: closeDeleteModal });

  const downloadMyData = async () => {
    setBusy("export");
    try {
      const res = await api.get("/dsr/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `functional-lab-export-${user?.id?.slice(0, 8) || "you"}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setBusy(""); }
  };

  const [dsrType, setDsrType] = useState("correction");
  const [dsrReason, setDsrReason] = useState("");
  const submitDsr = async () => {
    if (dsrReason.trim().length < 3) { toast.error("Please describe your request."); return; }
    setBusy("dsr");
    try {
      await api.post("/dsr/requests", { request_type: dsrType, reason: dsrReason });
      toast.success("Request submitted. An administrator will be in touch.");
      setDsrReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(""); }
  };

  const emailVerified = user?.email_verified === true;
  const canDeactivate = user?.role !== "admin";
  // Only patients may self-delete. Clinicians/admins retain records per policy.
  const canDelete = user?.role === "patient";
  const deletePhraseOk = deletePhrase.trim().toUpperCase() === "DELETE MY ACCOUNT";

  const restartTour = () => {
    if (!user?.id) return;
    // Remove the completion flag written by OnboardingTour when the patient
    // finished or dismissed it. Navigating to /app remounts the tour host
    // (DashLayout) and its useEffect will re-open the walkthrough after the
    // usual ~900ms settle delay.
    try {
      window.localStorage.removeItem(`fl_onboarding_done_${user.id}`);
    } catch { /* private-mode / disabled storage — user can still navigate */ }
    toast.success("Restarting the Quick Start tour…");
    nav("/app");
  };

  return (
    <DashLayout>
      <div className="max-w-3xl">
        <div className="fl-label text-[#f47a20]">Security</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2">Security & privacy</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-xl">
          Two-factor authentication, active sessions, email verification and account controls. All changes are logged in your audit trail.
        </p>

        {/* Email verification */}
        <section className="fl-card p-6 mt-8" data-testid="sec-email-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="fl-label text-[#f47a20] flex items-center gap-2"><Mail size={12}/> Email verification</div>
              <div className="mt-2 text-sm">
                {emailVerified ? (
                  <span className="text-emerald-400" data-testid="sec-email-verified">Your email <b>{user?.email}</b> is verified.</span>
                ) : (
                  <span className="text-amber-400" data-testid="sec-email-unverified">Your email <b>{user?.email}</b> is not yet verified.</span>
                )}
              </div>
            </div>
            {!emailVerified && (
              <button onClick={resendEmail} disabled={busy === "resend"} className="fl-btn-anim fl-btn-ghost text-xs" data-testid="sec-email-resend">
                {busy === "resend" ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>} Resend link
              </button>
            )}
          </div>
        </section>

        {/* Contact details (self-service) */}
        <section className="fl-card p-6 mt-4" data-testid="sec-contact-card">
          <div className="fl-label text-[#f47a20] flex items-center gap-2"><Phone size={12} /> Contact details</div>
          <p className="text-xs text-zinc-500 mt-2">Your phone and address help your clinician reach you. Your email is managed from your account and isn't changed here.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div className="sm:col-span-2">
              <div className="fl-label mb-1">Phone number</div>
              <input className="fl-input" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="e.g. 07700 900123" data-testid="sec-contact-phone" />
            </div>
            <div className="sm:col-span-2">
              <div className="fl-label mb-1">Address line 1</div>
              <input className="fl-input" value={contact.address_line1} onChange={(e) => setContact({ ...contact, address_line1: e.target.value })} data-testid="sec-contact-line1" />
            </div>
            <div className="sm:col-span-2">
              <div className="fl-label mb-1">Address line 2 (optional)</div>
              <input className="fl-input" value={contact.address_line2} onChange={(e) => setContact({ ...contact, address_line2: e.target.value })} data-testid="sec-contact-line2" />
            </div>
            <div>
              <div className="fl-label mb-1">City / town</div>
              <input className="fl-input" value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} data-testid="sec-contact-city" />
            </div>
            <div>
              <div className="fl-label mb-1">Postcode</div>
              <input className="fl-input" value={contact.postcode} onChange={(e) => setContact({ ...contact, postcode: e.target.value })} data-testid="sec-contact-postcode" />
            </div>
            <div>
              <div className="fl-label mb-1">Country</div>
              <input className="fl-input" value={contact.country} onChange={(e) => setContact({ ...contact, country: e.target.value })} placeholder="United Kingdom" data-testid="sec-contact-country" />
            </div>
          </div>
          <button onClick={saveContact} disabled={busy === "contact"} className="fl-btn mt-4" data-testid="sec-contact-save">
            {busy === "contact" ? <Loader2 size={14} className="animate-spin" /> : null} Save contact details
          </button>
        </section>

        {/* Communication preferences (patient-facing retention/notification controls) */}
        <section className="fl-card p-6 mt-4" data-testid="sec-comm-prefs-card">
          <div className="fl-label text-[#f47a20] flex items-center gap-2"><Mail size={12} /> Communication preferences</div>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Choose how Functional LAB keeps in touch about your care. You can change these at any time.
          </p>
          <div className="mt-4 space-y-3 max-w-xl">
            {[
              { k: "follow_up_reminders_enabled", label: "Care follow-up reminders", desc: "Receive reminders when your rehabilitation review may be due or after a cancelled appointment." },
              { k: "session_review_notifications_enabled", label: "Session review updates", desc: "Let me know when my clinician publishes a new session review." },
              { k: "email_enabled", label: "Email channel", desc: "Allow Functional LAB to contact me by email for the options above." },
              { k: "in_app_enabled", label: "In-app notifications", desc: "Show these updates inside the Functional LAB app." },
            ].map((row) => (
              <label key={row.k} className="flex items-start gap-3 cursor-pointer" data-testid={`comm-pref-${row.k}`}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!commPrefs[row.k]}
                  onChange={(e) => saveCommPref(row.k, e.target.checked)}
                  disabled={busy === "comm-prefs"}
                  data-testid={`comm-pref-toggle-${row.k}`}
                />
                <span>
                  <span className="block text-sm text-zinc-200">{row.label}</span>
                  <span className="block text-xs text-zinc-500">{row.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        {/* Change password (self-service, all roles) */}
        <section className="fl-card p-6 mt-4" data-testid="sec-change-pw-card">
          <div className="fl-label text-[#f47a20] flex items-center gap-2"><KeyRound size={12}/> Change password</div>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Update your account password. You'll stay signed in on this device — other active sessions will need to sign in again with the new password on next check.
          </p>
          <div className="mt-4 grid md:grid-cols-3 gap-2">
            <input className="fl-input" type="password" placeholder="Current password" autoComplete="current-password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} data-testid="sec-pw-current"/>
            <input className="fl-input" type="password" placeholder="New password (min 8 chars)" autoComplete="new-password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} data-testid="sec-pw-new"/>
            <input className="fl-input" type="password" placeholder="Confirm new password" autoComplete="new-password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} data-testid="sec-pw-confirm"/>
          </div>
          <button
            onClick={changePassword}
            disabled={busy === "change-pw" || !pwCurrent || pwNew.length < 8 || pwNew !== pwConfirm}
            className="fl-btn text-sm mt-3"
            data-testid="sec-pw-change-btn"
          >
            {busy === "change-pw" ? <Loader2 size={12} className="animate-spin"/> : <KeyRound size={12}/>} Change password
          </button>
        </section>

        {/* MFA */}
        <section className="fl-card p-6 mt-4" data-testid="sec-mfa-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="fl-label text-[#f47a20] flex items-center gap-2"><ShieldCheck size={12}/> Two-factor authentication</div>
              <div className="mt-2 text-sm text-zinc-300">
                {mfaStatus.enabled ? (
                  <span className="text-emerald-400" data-testid="sec-mfa-on">Enabled — you'll be asked for a code from your authenticator app after signing in.</span>
                ) : (
                  <span data-testid="sec-mfa-off">Disabled. {mfaStatus.required && <b className="text-amber-400">Recommended for clinicians and admins.</b>}</span>
                )}
              </div>
            </div>
          </div>

          {!mfaStatus.enabled && !setup && (
            <button onClick={beginSetup} disabled={busy === "setup"} className="fl-btn mt-4" data-testid="sec-mfa-begin">
              {busy === "setup" ? <Loader2 size={12} className="animate-spin"/> : <ShieldCheck size={14}/>} Set up two-factor
            </button>
          )}

          {!mfaStatus.enabled && setup && (
            <div className="mt-6 p-4 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }} data-testid="sec-mfa-setup">
              <div className="flex flex-col md:flex-row gap-6">
                <div
                  className="w-40 h-40 shrink-0 bg-white p-3 rounded flex items-center justify-center"
                  role="img"
                  aria-label="Multi-factor authentication QR code — scan with your authenticator app or enter the secret shown to the right"
                >
                  <QRCodeSVG value={setup.provisioning_uri} size={144} level="M" data-testid="sec-mfa-qr" />
                </div>
                <div className="text-sm">
                  <div className="fl-label mb-2">Step 1</div>
                  <p className="text-zinc-300">Scan the QR with Google Authenticator, Authy, 1Password or any TOTP app.</p>
                  <div className="fl-label mt-4 mb-2">Or enter this secret manually</div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-black/40 px-2 py-1 rounded border border-white/10 select-all" data-testid="sec-mfa-secret">{setup.secret}</code>
                    <button onClick={() => { navigator.clipboard?.writeText(setup.secret); toast.success("Copied"); }} className="text-zinc-500 hover:text-[#f47a20]" data-testid="sec-mfa-copy"><Copy size={12}/></button>
                  </div>
                  <div className="fl-label mt-4 mb-2">Step 2 · Enter the 6-digit code</div>
                  <input
                    className="fl-input tracking-[0.6em] text-center max-w-[10rem]"
                    inputMode="numeric" pattern="\d{6}" maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="sec-mfa-verify-code"
                  />
                  <div className="mt-3 flex gap-2">
                    <button onClick={confirmSetup} disabled={busy === "verify" || verifyCode.length !== 6} className="fl-btn text-sm" data-testid="sec-mfa-verify-btn">
                      {busy === "verify" ? <Loader2 size={12} className="animate-spin"/> : "Enable 2FA"}
                    </button>
                    <button onClick={() => { setSetup(null); setVerifyCode(""); }} className="fl-btn-ghost text-sm" data-testid="sec-mfa-cancel-setup">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {mfaStatus.enabled && (
            <div className="mt-4 p-4 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }} data-testid="sec-mfa-disable">
              <div className="fl-label mb-2 flex items-center gap-2"><ShieldOff size={12}/> Disable two-factor</div>
              <p className="text-xs text-zinc-500 mb-3">Confirm your password AND a fresh authenticator code to disable.</p>
              <div className="grid md:grid-cols-2 gap-2">
                <input className="fl-input" type="password" placeholder="Password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} data-testid="sec-mfa-disable-pw"/>
                <input className="fl-input tracking-[0.4em] text-center" inputMode="numeric" pattern="\d{6}" maxLength={6} placeholder="6-digit code" value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))} data-testid="sec-mfa-disable-code"/>
              </div>
              <button onClick={disableMfa} disabled={busy === "disable" || !disablePw || disableCode.length !== 6} className="fl-btn-ghost mt-3 text-sm" data-testid="sec-mfa-disable-btn">
                {busy === "disable" ? <Loader2 size={12} className="animate-spin"/> : "Disable 2FA"}
              </button>
            </div>
          )}
        </section>

        {/* Sessions */}
        <section className="fl-card p-6 mt-4" data-testid="sec-sessions-card">
          <div className="fl-label text-[#f47a20] flex items-center gap-2"><LogOut size={12}/> Active sessions</div>
          <p className="text-xs text-zinc-500 mt-1">Every device where you're currently signed in. Revoke anything you don't recognise.</p>
          <div className="mt-4 space-y-2">
            {sessions.length === 0 && <div className="text-xs text-zinc-500">No active sessions.</div>}
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 p-3 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }} data-testid={`sec-session-${s.id}`}>
                <div className="text-sm">
                  <div className="font-medium">
                    {s.current ? <span className="text-emerald-400">This device</span> : <span className="text-zinc-300">Other device</span>}
                    <span className="text-zinc-500 ml-2 text-xs">· {s.method}</span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">IP {s.ip} · last seen {new Date(s.last_seen_at).toLocaleString()}</div>
                </div>
                {!s.current && (
                  <button onClick={() => revokeSession(s.id)} disabled={busy === `revoke-${s.id}`} className="fl-btn-ghost text-xs" data-testid={`sec-session-revoke-${s.id}`}>
                    {busy === `revoke-${s.id}` ? <Loader2 size={12} className="animate-spin"/> : "Revoke"}
                  </button>
                )}
              </div>
            ))}
          </div>
          {sessions.filter((s) => !s.current).length > 0 && (
            <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--fl-border)" }}>
              <div className="fl-label mb-2">Sign out of everywhere else</div>
              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <input className="fl-input" type="password" placeholder="Confirm password" value={revokeAllPw} onChange={(e) => setRevokeAllPw(e.target.value)} data-testid="sec-revoke-all-pw"/>
                <button onClick={revokeAllOthers} disabled={busy === "revoke-all" || !revokeAllPw} className="fl-btn-ghost text-sm shrink-0" data-testid="sec-revoke-all-btn">
                  {busy === "revoke-all" ? <Loader2 size={12} className="animate-spin"/> : "Sign out other sessions"}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Data & privacy — Phase D */}
        <section className="fl-card p-6 mt-4" data-testid="sec-privacy-card">
          <div className="fl-label text-[#f47a20] flex items-center gap-2"><FileText size={12}/> Your data & privacy rights</div>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Download a copy of your data, or ask us to correct, restrict or delete it. Deletion requests for clinical records go to a clinical administrator for review and cannot be actioned automatically — see the retention schedule below.
          </p>

          <div className="mt-4">
            <button onClick={downloadMyData} disabled={busy === "export"} className="fl-btn text-sm" data-testid="sec-download-data">
              {busy === "export" ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} Download my data (JSON)
            </button>
          </div>

          <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--fl-border)" }}>
            <div className="fl-label mb-2">Submit a request</div>
            <div className="grid md:grid-cols-3 gap-2 items-start">
              <select className="fl-input" value={dsrType} onChange={(e) => setDsrType(e.target.value)} data-testid="sec-dsr-type">
                <option value="correction">Correction</option>
                <option value="restriction">Restriction of processing</option>
                <option value="deletion">Deletion (routed to review)</option>
                <option value="portability">Portability (structured export)</option>
                <option value="account_closure">Account closure</option>
              </select>
              <textarea className="fl-input md:col-span-2" rows={3} placeholder="Briefly describe your request" value={dsrReason} onChange={(e) => setDsrReason(e.target.value)} data-testid="sec-dsr-reason"/>
            </div>
            <button onClick={submitDsr} disabled={busy === "dsr" || dsrReason.trim().length < 3} className="fl-btn-ghost text-sm mt-3" data-testid="sec-dsr-submit">
              {busy === "dsr" ? <Loader2 size={12} className="animate-spin"/> : "Submit request"}
            </button>
          </div>
        </section>

        {/* Patient tools — Quick Start tour replay */}
        {user?.role === "patient" && (
          <section className="fl-card p-6 mt-4" data-testid="sec-tour-card">
            <div className="fl-label text-[color:var(--fl-orange)] flex items-center gap-2"><Rocket size={12}/> Quick Start tour</div>
            <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--fl-muted)" }}>
              Replay the first-login walkthrough that introduces every tab in your dashboard. Useful if you skipped it or you just want a refresher.
            </p>
            <button
              onClick={restartTour}
              className="fl-btn-ghost mt-4 text-sm"
              data-testid="sec-tour-restart"
            >
              <Rocket size={12}/> Restart tour
            </button>
          </section>
        )}

        {/* Deactivate */}
        {canDeactivate && (
          <section className="fl-card p-6 mt-4 border-red-900/40" data-testid="sec-deactivate-card">
            <div className="fl-label text-red-400 flex items-center gap-2"><Trash2 size={12}/> Deactivate account</div>
            <p className="text-xs text-zinc-500 mt-1 max-w-xl">
              Deactivation immediately signs you out of every device and prevents sign-in. Your clinical data is retained under our retention policy — contact an administrator to request full deletion.
            </p>
            <div className="mt-4 grid md:grid-cols-2 gap-2">
              <input className="fl-input" type="password" placeholder="Confirm password" value={deactivatePw} onChange={(e) => setDeactivatePw(e.target.value)} data-testid="sec-deactivate-pw"/>
              <label className="flex items-center gap-2 text-xs text-zinc-400">
                <input type="checkbox" checked={deactivateConfirm} onChange={(e) => setDeactivateConfirm(e.target.checked)} data-testid="sec-deactivate-confirm"/>
                I understand this action can only be reversed by an administrator.
              </label>
            </div>
            <button onClick={deactivateAccount} disabled={busy === "deactivate" || !deactivatePw || !deactivateConfirm} className="fl-btn-ghost mt-3 text-sm border-red-800 text-red-400" data-testid="sec-deactivate-btn">
              {busy === "deactivate" ? <Loader2 size={12} className="animate-spin"/> : "Deactivate my account"}
            </button>
          </section>
        )}

        {/* Delete permanently — GDPR right to erasure (patients only) */}
        {canDelete && (
          <section className="fl-card p-6 mt-4 border-red-900/40" data-testid="sec-delete-card" style={{ borderColor: "rgba(220, 38, 38, 0.4)" }}>
            <div className="fl-label text-red-500 flex items-center gap-2"><Trash2 size={12}/> Delete my account permanently</div>
            <p className="text-xs text-zinc-500 mt-1 max-w-xl">
              This <b className="text-red-400">permanently erases</b> your account and all personal data we hold about you — health metrics, session logs, videos, messages, notifications, wearable connections and consent records. Conversations with your clinician will be anonymised so they still see their side of the thread but cannot identify you. <b>This action cannot be undone.</b>
            </p>
            <button
              onClick={() => {
                // Force-close the onboarding tour if it's still active so its
                // keyboard focus-trap doesn't steal focus from our inputs.
                try { window.dispatchEvent(new CustomEvent("fl:close-onboarding")); } catch { /* ignore */ }
                setShowDeleteModal(true);
              }}
              className="fl-btn-ghost mt-4 text-sm border-red-700 text-red-500 hover:bg-red-950/40"
              data-testid="sec-delete-open"
            >
              <Trash2 size={12}/> Delete my account…
            </button>
          </section>
        )}

        {/* Delete confirmation modal */}
        {showDeleteModal && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.75)" }}
            data-testid="sec-delete-modal"
            onClick={closeDeleteModal}
          >
            <div
              ref={deleteDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-modal-title"
              className="fl-card fl-card-elevated p-6 max-w-lg w-full"
              style={{ background: "var(--fl-bg-1)", border: "1px solid rgba(220, 38, 38, 0.5)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div id="delete-modal-title" className="fl-label text-red-500 flex items-center gap-2"><Trash2 size={14}/> Confirm permanent deletion</div>
              <p className="text-sm text-zinc-300 mt-3">
                You are about to <b className="text-red-400">permanently delete</b> the account <b>{user?.email}</b>.
              </p>
              <ul className="text-xs text-zinc-500 mt-3 space-y-1 list-disc pl-4">
                <li>All health metrics, exercise logs, videos and progress records will be erased.</li>
                <li>Wearable connections, notifications, forms and consents will be removed.</li>
                <li>Messages will be anonymised — your clinician still sees their side of the thread but not your identity.</li>
                <li>This action is <b>irreversible</b>. You will be signed out immediately.</li>
              </ul>

              {/* Optional exit survey — anonymous churn signal, never linked to user_id */}
              <div
                className="mt-5 p-4 rounded-md"
                style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }}
                data-testid="sec-delete-exit-survey"
              >
                <div className="fl-label text-zinc-400">Optional — help us improve</div>
                <p className="text-xs text-zinc-500 mt-1">
                  Not required. Anonymous — never linked back to you once your account is erased.
                </p>
                <label className="fl-label block mt-3 mb-1">Reason for leaving</label>
                <select
                  className="fl-input"
                  value={exitReason}
                  onChange={(e) => setExitReason(e.target.value)}
                  data-testid="sec-delete-exit-reason"
                >
                  <option value="">— prefer not to say —</option>
                  <option value="too_expensive">Too expensive</option>
                  <option value="no_longer_needed">No longer need it</option>
                  <option value="missing_feature">Missing a feature I need</option>
                  <option value="poor_experience">Poor experience</option>
                  <option value="privacy_concerns">Privacy concerns</option>
                  <option value="switching_provider">Switching to another provider</option>
                  <option value="other">Other</option>
                </select>
                <label className="fl-label block mt-3 mb-1">Anything else? (500 chars max)</label>
                <textarea
                  className="fl-input"
                  rows={2}
                  maxLength={500}
                  value={exitNote}
                  onChange={(e) => setExitNote(e.target.value)}
                  placeholder="What would have made this better?"
                  data-testid="sec-delete-exit-note"
                />
              </div>

              <div className="mt-5 space-y-3">
                <div>
                  <label className="fl-label block mb-1">Type <code className="text-red-400">DELETE MY ACCOUNT</code> to confirm</label>
                  <input
                    className="fl-input"
                    value={deletePhrase}
                    onChange={(e) => setDeletePhrase(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    data-testid="sec-delete-phrase"
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="fl-label block mb-1">Confirm your password</label>
                  <input
                    className="fl-input"
                    type="password"
                    value={deletePw}
                    onChange={(e) => setDeletePw(e.target.value)}
                    data-testid="sec-delete-pw"
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  onClick={closeDeleteModal}
                  className="fl-btn-ghost text-sm"
                  data-testid="sec-delete-cancel"
                  disabled={busy === "delete"}
                >
                  Cancel
                </button>
                <button
                  onClick={deleteAccount}
                  disabled={busy === "delete" || !deletePhraseOk || !deletePw}
                  className="fl-btn text-sm bg-red-700 hover:bg-red-600 border-red-700"
                  style={{ background: deletePhraseOk && deletePw ? "#b91c1c" : undefined }}
                  data-testid="sec-delete-confirm"
                >
                  {busy === "delete" ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>} Delete permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashLayout>
  );
}
